// Libs
import React, { useEffect } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Components
import Select from '../../../../../Field/Select';
import Input from '../../../../../Field/Input';

// Constants
import {
  DAY_NUMBERS,
  SHIPPING_SCHEDULES,
  WEEK_DAYS,
} from '../../../../../../constants/cart';

// Validation schema
import { scheduleDetailsSchema } from '../../../../../../validationSchema';
import Disclaimer from '../../../OrderProcessingInfo/Disclaimer';

// Styles
import { useStyles } from './MonthlyStyles';

const DAYS_IN_MONTH = 31;

const Monthly = ({
  monthly,
  onChangeMonthlyOption = () => {},
  onChangeSelect = () => {},
  onChangeMonthCount = () => {},
  setMonthError = () => {},
  disclaimer,
  openOrderProcessingWindow,
}) => {
  const { classes, cx } = useStyles();

  const optionTitles = Object.keys(monthly);
  const checkedOptionTitle = optionTitles.find((item) => monthly[item].checked);

  const {
    control,
    trigger,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(scheduleDetailsSchema),
    defaultValues: {
      monthNumberForMonthDay: monthly?.monthDay?.monthNumber,
      monthNumberForWeekDay: monthly?.weekDay?.monthNumber,
    },
  });

  const { monthNumberForMonthDay, monthNumberForWeekDay } = errors;

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (monthly?.monthDay?.checked && monthNumberForMonthDay) {
      setMonthError(monthNumberForMonthDay?.message);
    } else if (monthly?.weekDay?.checked && monthNumberForWeekDay) {
      setMonthError(monthNumberForWeekDay?.message);
    } else {
      setMonthError('');
    }
  }, [setMonthError, monthNumberForMonthDay, monthNumberForWeekDay, monthly]);

  return (
    <RadioGroup
      className={classes.internalRadioGroup}
      value={checkedOptionTitle}
      row={false}
      aria-labelledby="address-format-radio-buttons-group-label"
      name="shipping-schedule-radio-buttons-group">
      <FormControlLabel
        className={classes.radioLabel}
        value={optionTitles[0]}
        control={
          <Radio
            classes={{
              root: classes.radioItem,
            }}
            onChange={onChangeMonthlyOption(0)}
          />
        }
        label={
          <div className={classes.container}>
            <span className={classes.span}>Day</span>
            <Select
              disabled={checkedOptionTitle !== 'monthDay'}
              initialValue={monthly?.monthDay?.dayNumber}
              classic
              required
              name="monthDay"
              onChange={onChangeSelect}
              className={cx(classes.select, classes.daySelect)}
              placeholder="Select"
              items={new Array(DAYS_IN_MONTH).fill(0).map((_, index) => ({
                title: index + 1,
                name: index + 1,
              }))}
            />
            Of Every
            <Controller
              name="monthNumberForMonthDay"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  disabled={checkedOptionTitle !== 'monthDay'}
                  maxLength={2}
                  onChange={onChangeMonthCount(field.onChange)}
                  name="monthNumberForMonthDay"
                  className={classes.input}
                  value={monthly?.monthDay?.monthNumber}
                  error={
                    SHIPPING_SCHEDULES[5].value &&
                    monthly?.monthDay?.checked &&
                    errors?.monthNumberForMonthDay?.message
                  }
                />
              )}
            />
            <span className={classes.fieldLabel}>Month(s)</span>
            {disclaimer && checkedOptionTitle === 'monthDay' && (
              <Disclaimer
                onClick={openOrderProcessingWindow}
                className={classes.disclaimer}
                disclaimer={disclaimer}
              />
            )}
          </div>
        }
      />
      <FormControlLabel
        className={classes.radioLabel}
        value={optionTitles[1]}
        control={
          <Radio
            classes={{
              root: classes.radioItem,
            }}
            onChange={onChangeMonthlyOption(1)}
          />
        }
        label={
          <div className={classes.container}>
            <span className={classes.span}>The</span>
            <Select
              disabled={checkedOptionTitle !== 'weekDay'}
              classic
              required
              initialValue={monthly?.weekDay?.dayNumber}
              className={cx(classes.select, classes.daySelect)}
              onChange={onChangeSelect}
              placeholder="Select"
              items={DAY_NUMBERS}
              name="weekDayNumber"
            />
            <Select
              disabled={checkedOptionTitle !== 'weekDay'}
              initialValue={monthly.weekDay.weekDay}
              classic
              required
              className={cx(classes.select, classes.weekDaySelect)}
              onChange={onChangeSelect}
              placeholder="Select"
              name="weekDayTitle"
              items={WEEK_DAYS}
            />
            Of Every
            <Controller
              name="monthNumberForWeekDay"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  disabled={checkedOptionTitle !== 'weekDay'}
                  maxLength={2}
                  name="monthNumberForWeekDay"
                  className={cx(classes.margin, classes.input)}
                  value={monthly?.weekDay?.monthNumber}
                  error={
                    SHIPPING_SCHEDULES[5].value &&
                    monthly?.weekDay?.checked &&
                    errors?.monthNumberForWeekDay?.message
                  }
                  onChange={onChangeMonthCount(field.onChange)}
                />
              )}
            />
            <span className={classes.fieldLabel}>Month(s)</span>
            {disclaimer && checkedOptionTitle === 'weekDay' && (
              <Disclaimer
                onClick={openOrderProcessingWindow}
                className={classes.weekDayDisclaimer}
                disclaimer={disclaimer}
              />
            )}
          </div>
        }
      />
    </RadioGroup>
  );
};

export default Monthly;
